// Client wrapper over the `cook-mode-chat` edge function.
//
// Called from the cook-mode chat sheet. Bundles the recipe context,
// the current step, the resolved canonicals for the step's
// ingredients, and the user's question. Returns a short answer.
//
// Per the cost-discipline rule, this fires ONLY on explicit user tap
// (chat input submit / quick-question chip) — never on sheet open or
// recipe-page render. The edge function caches first-turn answers
// keyed on (recipe.slug, step.id, normalized_question) so identical
// questions across users are zero-cost after the first ask.
//
// Canonical-data discipline: this file MUST go through findIngredient
// and getIngredientInfo (the sanctioned read entry points) — it is
// the single place where step-ingredients are resolved before the
// edge call. Edge function does not re-resolve.

import { supabase } from "./supabase";
import { findIngredient, getIngredientInfo } from "../data/ingredients";

function resolveStepIngredients(stepIngredients, dbInfoMap) {
  if (!Array.isArray(stepIngredients)) return [];
  return stepIngredients.slice(0, 10).map((ing) => {
    const out = {
      amount: ing?.amount || null,
      item:   ing?.item   || null,
    };
    const id = ing?.ingredientId || ing?.canonicalId || null;
    if (id) {
      const canonical = findIngredient(id);
      if (canonical) {
        const info = getIngredientInfo(canonical, dbInfoMap || null);
        out.canonical = {
          id,
          name: canonical.name || id,
          info: info || null,
        };
      }
    }
    return out;
  });
}

/**
 * Ask Claude a question about the current cook step.
 *
 * @param {object} args
 *   @param {object} args.recipe         — recipe blob (needs .slug)
 *   @param {object} args.step           — current step (needs .id)
 *   @param {string} args.question       — user's question
 *   @param {Array}  [args.priorTurns]   — [{q, a}, ...] for follow-ups
 *   @param {Map}    [args.dbInfoMap]    — useIngredientInfo().dbMap, used
 *                                         to merge live DB enrichment
 *                                         into each canonical's info.
 *
 * @returns {Promise<{ answer?: string, cached?: boolean, error?: string }>}
 */
export async function askCookChat({ recipe, step, question, priorTurns, dbInfoMap } = {}) {
  if (!recipe?.slug) return { error: "Missing recipe slug." };
  if (!step?.id)    return { error: "Missing step id." };
  const trimmed = typeof question === "string" ? question.trim() : "";
  if (!trimmed)     return { error: "Ask a question first." };

  const ingredients = resolveStepIngredients(step.ingredients, dbInfoMap);

  const body = {
    recipe: {
      slug:      recipe.slug,
      title:     recipe.title     || null,
      cuisine:   recipe.cuisine   || null,
      technique: Array.isArray(recipe.technique) ? recipe.technique.slice(0, 6) : [],
    },
    step: {
      id:          step.id,
      title:       step.title       || null,
      instruction: step.instruction || null,
      ingredients,
      timer:       step.timer       || null,
    },
    question: trimmed,
    priorTurns: Array.isArray(priorTurns)
      ? priorTurns.slice(-3).map((t) => ({ q: t?.q || "", a: t?.a || "" }))
      : [],
  };

  const { data, error } = await supabase.functions.invoke("cook-mode-chat", { body });

  if (error) {
    let detail = error.message || "unknown error";
    try {
      const ctx = error?.context;
      if (ctx && typeof ctx.json === "function") {
        const e = await ctx.json();
        if (e?.error) detail = e.error;
      }
    } catch { /* fall through */ }
    return { error: detail };
  }

  if (!data?.answer) {
    return { error: "No answer in response — try again." };
  }

  return { answer: data.answer, cached: !!data.cached };
}
